import 'twin.macro';
import { SelectCards } from 'components/Form/SelectCards';
import React, { useState } from 'react';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { ArrowRightIcon as ArrowRight } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import {
  CreateSchoolyearOptions,
  KeepItem,
} from 'containers/MesAnneesScolaires/types';
import { useEventCustomFieldDefinitions } from 'queries/events/customfields/useEventCustomFieldDefinitions';

interface Props {
  // Options is an object with keys corresponding to the KeepItem enum
  onSubmit: (options: CreateSchoolyearOptions) => void;
  onCancel: () => void;
  previousSchoolyearSingleSchool: {
    schoolId: string;
    schoolyearId: string;
  };
}

export function Step4(props: Props) {
  const { onSubmit, onCancel, previousSchoolyearSingleSchool } = props;
  const { t } = useTranslation(['common', 'schoolyearSettings']);
  const [keepList, setKeepList] = useState<KeepItem[]>([
    'keepLastYearEtablissement',
    'keepLastYearTags',
    'keepLastYearCarnetsDeNotes',
    'keepLastYearEmploisDuTemps',
  ]);
  const [customFieldDefIds, setCustomFieldDefIds] = useState<string[]>([]);

  const { data: customFieldDefs = [] } = useEventCustomFieldDefinitions({
    schoolyearId: previousSchoolyearSingleSchool.schoolyearId,
    schoolId: previousSchoolyearSingleSchool.schoolId,
  });

  return (
    <div>
      <div tw="mb-10">
        <SelectCards<KeepItem, true>
          options={[
            {
              label: t('schoolyearSettings:newSchoolYear.sameschool'),
              value: 'keepLastYearEtablissement',
            },
            {
              label: t('schoolyearSettings:newSchoolYear.copyTags'),
              value: 'keepLastYearTags',
            },
            {
              label: t('schoolyearSettings:newSchoolYear.copyCarnet'),
              value: 'keepLastYearCarnetsDeNotes',
            },
            {
              label: t('schoolyearSettings:newSchoolYear.copyEdt'),
              value: 'keepLastYearEmploisDuTemps',
            },
          ]}
          value={keepList}
          onChange={setKeepList}
          size="big"
          direction="vertical"
          isMulti
        />
        {customFieldDefs.length > 0 && (
          <div tw="mt-4">
            <SelectCards<string, true>
              value={
                customFieldDefIds.length > 0
                  ? ['keepLastYearCustomFieldDefs']
                  : []
              }
              options={[
                {
                  label: t(
                    'schoolyearSettings:newSchoolYear.copyCustomFieldDefinitions',
                  ),
                  value: 'keepLastYearCustomFieldDefs',
                  description: t(
                    'schoolyearSettings:newSchoolYear.customFieldDefinitionsDetail',
                    { count: customFieldDefs.length },
                  ),
                },
              ]}
              id="keepLastYearCustomFieldDefs"
              onChange={() => {
                if (customFieldDefIds.length === 0) {
                  const allCustomFieldsIds = customFieldDefs.map(df => df.id);
                  setCustomFieldDefIds(allCustomFieldsIds);
                } else {
                  setCustomFieldDefIds([]);
                }
              }}
              size="big"
              direction="vertical"
              isMulti
            />
          </div>
        )}
        {customFieldDefIds.length > 0 && (
          <div tw="mt-1 ml-8 border-l pl-2 border-neutral-moderate">
            <SelectCards<string, true>
              isMulti
              options={customFieldDefs.map(df => {
                const cfType = t(`common:customFields.types.${df.type}`);
                const cfDetail =
                  df.type === 'select' && df.options.length > 0
                    ? ` (${df.options.map(opt => opt.value).join(', ')})`
                    : '';
                return {
                  label: df.name,
                  value: df.id,
                  description: `${cfType}${cfDetail}`,
                };
              })}
              value={customFieldDefIds}
              onChange={setCustomFieldDefIds}
              direction="vertical"
              size="big"
              spacing="medium"
            />
          </div>
        )}
      </div>
      <div tw="flex justify-center space-x-4 items-center">
        <Button large minimal onClick={onCancel}>
          {t('common:buttons.previous')}
        </Button>
        <Button
          intent="primary"
          onClick={() =>
            onSubmit({
              keepLastYearEtablissement: keepList.includes(
                'keepLastYearEtablissement',
              ),
              keepLastYearTags: keepList.includes('keepLastYearTags'),
              keepLastYearCarnetsDeNotes: keepList.includes(
                'keepLastYearCarnetsDeNotes',
              ),
              keepLastYearEmploisDuTemps: keepList.includes(
                'keepLastYearEmploisDuTemps',
              ),
              keepLastYearCustomFieldDefinitionIds:
                customFieldDefIds.length > 0 ? customFieldDefIds : undefined,
            })
          }
          large
          rightIcon={ArrowRight}
        >
          {t('common:buttons.create')}
        </Button>
      </div>
    </div>
  );
}
